/* eslint-disable react-hooks/exhaustive-deps */
import './producer-brokerage-info.scss';

import { Box, Stack } from '@mui/material';
import {
  ContactType,
  UserGroup,
  useGetBDMRegion,
  useGetBrokerageOptionsList,
  useGetRegion,
  useGetUsers,
} from '@converge/graphql';
import {
  CreateBrokerageField,
  getCreateBrokerageFieldProps,
} from '@converge/data';
import {
  NewCountryFormSelectField,
  NewFormTextField,
  NewRegionFormSelectField,
  NewTypeAhead,
} from '../../presentation';
import { useEffect, useMemo, useState } from 'react';

import { AddressContainer } from './address-container';
import { BrokerageParams } from './brokerage-tabs';
import { CreateBrokerageFields } from './commission-container';
import { IPerson } from './accordion.form-container';
import { PhoneExtField } from '../../presentation';
import { UseFormReturn } from 'react-hook-form';
import { config } from '@converge/config';
import { getUserOptions } from '../submission';
import { useParams } from 'react-router-dom';

export interface ILicenseData {
  id: number | null | undefined;
  licenseType: string;
  licenseNumber: string;
  licenseRegionName: string;
  licenseRegionUuid: string;
  expirationDate: string;
  source: string;
  licenseTypeUuid: string;
}
export interface ProducerBrokerageInfosProps {
  setIsSameAddress: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLicenseBrokerage: React.Dispatch<React.SetStateAction<boolean>>;
  formMethods: UseFormReturn<CreateBrokerageFields, unknown>;
  isSameAddress: boolean;
  isReadOnly?: boolean;
  brokers: IPerson[];
  brokerageBillingStreet1?: string | null;
  brokerageBillingStreet2?: string | null;
  brokerageBillingCity?: string | null;
  brokerageBillingPostalCode?: string | null;
  brokerageBillingCountry?: string | null;
  brokerageBillingRegion?: string | null;
  parentBrokerageName?: string | null;
  partnerBrokerageName?: string | null;
}
const ProducerBrokerageInfo = ({
  setIsSameAddress,
  setIsLicenseBrokerage,
  formMethods,
  isSameAddress,
  isReadOnly,
  brokers,
  brokerageBillingStreet1,
  brokerageBillingStreet2,
  brokerageBillingCity,
  brokerageBillingPostalCode,
  brokerageBillingCountry,
  brokerageBillingRegion,
  parentBrokerageName,
  partnerBrokerageName,
}: ProducerBrokerageInfosProps) => {
  const [isLicensed, setIsLicensed] = useState(true);
  const { parentData, loading } = useGetBrokerageOptionsList();
  const { brokerageUuid } = useParams<keyof BrokerageParams>();
  const { data: region, loading: isUWRegionsLoading } = useGetRegion();
  const { data: bdmRegion, loading: isBDMRegionsLoading } = useGetBDMRegion();
  const { data: underwriterList, loading: isUnderwriterListLoading } =
    useGetUsers({ userGroups: [UserGroup.Underwriter] });
  const { data: businessDevManager, loading: isBDMListLoading } = useGetUsers({
    userGroups: [UserGroup.Bdm],
  });

  // Get current selected values from form
  const currentParentValue = formMethods.getValues(
    CreateBrokerageField.GlobalParentCompany
  );
  const currentPartnerValue = formMethods.getValues(
    CreateBrokerageField.PartnerEntity
  );

  const PARENTS_ENTITY_OPTIONS = useMemo(() => {
    let options =
      parentData
        ?.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
        .map((parentOption) => {
          return {
            value: parentOption.value,
            label: parentOption.name ?? '--',
          };
        }) || [];

    if (loading && brokerageUuid) {
      const immediateOptions = [];

      if (
        currentParentValue &&
        !options.find((opt) => opt.value === currentParentValue)
      ) {
        immediateOptions.push({
          value: currentParentValue,
          label: parentBrokerageName || 'Loading...',
        });
      }

      if (
        currentPartnerValue &&
        !options.find((opt) => opt.value === currentPartnerValue)
      ) {
        immediateOptions.push({
          value: currentPartnerValue,
          label: partnerBrokerageName || 'Loading...',
        });
      }

      options = [...immediateOptions, ...options];
    }

    return options;
  }, [
    parentData,
    loading,
    brokerageUuid,
    currentParentValue,
    currentPartnerValue,
    parentBrokerageName,
    partnerBrokerageName,
  ]);

  const UNDERWRITING_BDM_REGION_OPTIONS = bdmRegion?.bdmTerritories?.edges
    ?.map((bdmRegionOption) => {
      return {
        value: bdmRegionOption?.node?.uuid || '',
        label: bdmRegionOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const UNDERWRITING_REGION_OPTIONS = region?.uwTerritories?.edges
    ?.map((parentOption) => {
      return {
        value: parentOption?.node?.uuid || '',
        label: parentOption?.node?.name || '',
      };
    })
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const brokerList = brokers
    ?.map((broker) => ({
      label: `${broker.node.firstName} ${broker.node.lastName}`,
      value: broker.node.uuid,
      data: broker.node.contacts?.edges
        .filter((item) => item.node.type === ContactType.Email)
        .map((email) => ({
          label: email.node.value,
          value: email.node.uuid,
        })),
    }))
    ?.sort((a, b) => a?.label.localeCompare(b?.label));

  const UNDERWRITER_OPTIONS = useMemo(
    () => getUserOptions(underwriterList) ?? [],
    [underwriterList]
  );

  const BUSINESS_DEV_MANAGER_OPTIONS = useMemo(
    () => getUserOptions(businessDevManager) ?? [],
    [businessDevManager]
  );

  const primaryStreet1 = formMethods.getValues(
    CreateBrokerageField.PrimaryStreet1
  );

  const primaryStreet2 = formMethods.getValues(
    CreateBrokerageField.PrimaryStreet2
  );
  const primaryCity = formMethods.getValues(CreateBrokerageField.PrimaryCity);
  const primaryRegion = formMethods.getValues(
    CreateBrokerageField.PrimaryRegionUuid
  );
  const primaryCountry = formMethods.getValues(
    CreateBrokerageField.PrimaryCountryUuid
  );
  const primaryPostalCode = formMethods.getValues(
    CreateBrokerageField.PrimaryPostalCode
  );
  const [sameBilling, setSameBilling] = useState(isSameAddress ?? false);

  // Billing street 1 update
  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.Street1, primaryStreet1);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.Street1,
          brokerageBillingStreet1 || ''
        );
      } else {
        formMethods.setValue(CreateBrokerageField.Street1, '');
      }
    }
  }, [
    primaryStreet1,
    sameBilling,
    brokerageUuid,
    formMethods,
    brokerageBillingStreet1,
  ]);

  // Billing street 2 update
  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.Street2, primaryStreet2);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.Street2,
          brokerageBillingStreet2 || ''
        );
      } else {
        formMethods.setValue(CreateBrokerageField.Street2, '');
      }
    }
  }, [
    primaryStreet2,
    sameBilling,
    brokerageUuid,
    formMethods,
    brokerageBillingStreet2,
  ]);

  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.City, primaryCity);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.City,
          brokerageBillingCity || ''
        );
      } else {
        formMethods.setValue(CreateBrokerageField.City, '');
      }
    }
  }, [
    primaryCity,
    sameBilling,
    brokerageUuid,
    formMethods,
    brokerageBillingCity,
  ]);

  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.PostalCode, primaryPostalCode);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.PostalCode,
          brokerageBillingPostalCode || ''
        );
      } else {
        formMethods.setValue(CreateBrokerageField.PostalCode, '');
      }
    }
  }, [
    primaryPostalCode,
    sameBilling,
    brokerageUuid,
    formMethods,
    brokerageBillingPostalCode,
  ]);
  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.RegionUuid, primaryRegion);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.RegionUuid,
          brokerageBillingRegion || ''
        );
      } else {
        formMethods.setValue(CreateBrokerageField.RegionUuid, '');
      }
    }
  }, [
    primaryRegion,
    sameBilling,
    brokerageUuid,
    formMethods,
    brokerageBillingRegion,
  ]);
  useEffect(() => {
    if (sameBilling) {
      formMethods.setValue(CreateBrokerageField.CountryUuid, primaryCountry);
    } else {
      if (brokerageUuid) {
        formMethods.setValue(
          CreateBrokerageField.CountryUuid,
          brokerageBillingCountry || ''
        );
      } else {
        formMethods.setValue(
          CreateBrokerageField.CountryUuid,
          'bb3ca70e-1df7-40a5-966e-f82ebc541cd3'
        );
      }
    }
  }, [
    primaryCountry,
    formMethods,
    sameBilling,
    brokerageUuid,
    brokerageBillingCountry,
  ]);

  useEffect(() => {
    setSameBilling(isSameAddress);
  }, [isSameAddress]);

  return (
    <div className="broker-wrapper">
      <Stack className="brokerage-row-uw">
        <Box className="brokerage-business-manager">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.BusinessDevelopmentManager
            )}
            placeholder="Select BDM"
            dataTestId="brokerage-bdm-dropdown"
            options={BUSINESS_DEV_MANAGER_OPTIONS || []}
            disabled={isReadOnly}
            isLoading={isBDMListLoading}
            tooltip={
              'This Business Development Manager has been manually selected.'
            }
          />
        </Box>
        <Box className="brokerage-bdm-region">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(CreateBrokerageField.BdmRegion)}
            disabled={isReadOnly}
            options={UNDERWRITING_BDM_REGION_OPTIONS || []}
            placeholder="Select BDM Region"
            dataTestId="brokerage-bdm-region-dropdown"
            isLoading={isBDMRegionsLoading}
          />
        </Box>
        <Box className="brokerage-underwriter">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(CreateBrokerageField.Underwriter)}
            placeholder="Select UW"
            dataTestId="brokerage-underwriter-dropdown"
            options={UNDERWRITER_OPTIONS || []}
            disabled={isReadOnly}
            isLoading={isUnderwriterListLoading}
            tooltip={'This Underwriter has been manually selected.'}
          />
        </Box>
        <Box className="brokerage-uw-region">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(CreateBrokerageField.UwRegion)}
            disabled={isReadOnly}
            options={UNDERWRITING_REGION_OPTIONS || []}
            placeholder="Select UW Region"
            dataTestId="brokerage-uw-region-dropdown"
            isLoading={isUWRegionsLoading}
          />
        </Box>
      </Stack>
      <Stack className="producer-brokerage-info-row">
        <Box className="company-name">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.CompanyName)}
            required
            autoComplete={false}
            placeholder="Enter Company Name"
            dataTestId="company-name-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="dba-name">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.DbaName)}
            autoComplete={false}
            placeholder="Enter DBA Name"
            dataTestId="dba-name-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="global-parent">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.GlobalParentCompany
            )}
            options={PARENTS_ENTITY_OPTIONS}
            placeholder="Select if Applicable"
            dataTestId="global-parent-dropdown"
            disabled={isReadOnly}
            isLoading={loading}
            tooltip={
              'The parent entity must be created and saved separately before it can be selected here.'
            }
          />
        </Box>
        <Box className="global-parent">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PartnerEntity
            )}
            options={PARENTS_ENTITY_OPTIONS}
            placeholder="Select if Applicable"
            dataTestId="partner-entity-dropdown"
            disabled={isReadOnly}
            isLoading={loading}
            tooltip={
              'Select a Partner Entity if this brokerage will use the license(s) from another brokerage.'
            }
          />
        </Box>
      </Stack>
      <Stack className="producer-brokerage-info-row">
        <Box className="domain">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.Domain)}
            autoComplete={false}
            placeholder="domain.com"
            dataTestId="domain-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="fein">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.Fein)}
            autoComplete={false}
            placeholder="Enter FEIN"
            dataTestId="fein-input"
            isNumber
            disabled={isReadOnly}
          />
        </Box>
        <Box className="npn">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.Npn)}
            autoComplete={false}
            placeholder="Enter NPN"
            dataTestId="npn-input"
            isNumber
            disabled={isReadOnly}
          />
        </Box>
      </Stack>
      <Stack className="producer-brokerage-info-row">
        <Box className="primary-contact">
          <NewTypeAhead
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryContactUuid
            )}
            options={brokerList || []}
            placeholder="Select Contact"
            dataTestId="primary-contact-dropdown"
            disabled={isReadOnly}
            onChangeOption={(item) => {
              if (Array.isArray(item?.data) && item.data.length > 0) {
                formMethods.setValue(
                  CreateBrokerageField.PrimaryContactEmail,
                  item?.data[0]?.label ?? ''
                );
              } else {
                formMethods.setValue(
                  CreateBrokerageField.PrimaryContactEmail,
                  ''
                );
              }
            }}
          />
        </Box>
        <Box className="primary-email">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryContactEmail
            )}
            autoComplete={false}
            placeholder="name@domain.com"
            dataTestId="primary-contact-email-input"
            disabled={true}
          />
        </Box>
        <PhoneExtField
          disabled={isReadOnly}
          dataTestId="primary-phone-input"
          placeholder="415-555-1212"
          className="phone-field"
          mainClassName="primary-phone"
          autoComplete={false}
          dataExtTestId="primary-phone-ext-input"
          classNameExt="mobile-phone-extn"
          fieldPropsIdx={CreateBrokerageField.PrimaryPhoneNumber}
          fieldPropsExtIdx={CreateBrokerageField.PrimaryPhoneNumberExtn}
        />
      </Stack>
      <Stack className="producer-brokerage-info-row">
        <Box className="primary-address">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryStreet1
            )}
            required
            autoComplete={false}
            placeholder="1234 Main St"
            dataTestId="primary-street1-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="primary-address-2">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryStreet2
            )}
            autoComplete={false}
            placeholder="Suite 123"
            dataTestId="primary-street2-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="city">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(CreateBrokerageField.PrimaryCity)}
            autoComplete={false}
            required
            placeholder="City"
            dataTestId="primary-city-input"
            disabled={isReadOnly}
          />
        </Box>
      </Stack>
      <Stack className="producer-brokerage-info-row">
        <Box className="state">
          <NewRegionFormSelectField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryRegionUuid
            )}
            required
            dataTestId="primary-state-dropdown"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="postal-code">
          <NewFormTextField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryPostalCode
            )}
            autoComplete={false}
            required
            placeholder="Enter Postal or ZIP Code"
            dataTestId="primary-postal-code-input"
            disabled={isReadOnly}
          />
        </Box>
        <Box className="country">
          <NewCountryFormSelectField
            {...getCreateBrokerageFieldProps(
              CreateBrokerageField.PrimaryCountryUuid
            )}
            dataTestId="primary-country-dropdown"
            required
            disabled={isReadOnly}
          />
        </Box>
      </Stack>
      <Stack className="same-billing margin-b-20">
        <Box>
          {sameBilling === true && (
            <img
              data-testid="billing-same-as-primary-checkbox"
              width="20px"
              src={config.checkBox}
              alt="checkBox"
              className={`${isReadOnly ? 'checkbox-disabled' : 'checkbox'}`}
              onClick={() => {
                if (!isReadOnly) {
                  setSameBilling(false);
                  setIsSameAddress(!sameBilling);
                }
              }}
            />
          )}
          {sameBilling === false && (
            <img
              data-testid="billing-same-as-primary-checkbox"
              width="20px"
              src={config.unselectedCheckBox}
              alt="checkBox"
              className={`${isReadOnly ? 'checkbox-disabled' : 'checkbox'}`}
              onClick={() => {
                if (!isReadOnly) {
                  setSameBilling(true);
                  setIsSameAddress(!sameBilling);
                }
              }}
            />
          )}
        </Box>
        <Box className={`${isReadOnly ? 'text disabled-text' : 'text'}`}>
          Billing address same as above
        </Box>
      </Stack>
      {sameBilling === false && (
        <Stack>
          <AddressContainer isReadOnly={isReadOnly} />
        </Stack>
      )}
      <Stack className="same-billing">
        {isLicensed === true && (
          <img
            data-testid="licensed-brokerage-checkbox"
            width="20px"
            src={config.checkBox}
            alt="checkBox"
            className={`${isReadOnly ? 'checkbox-disabled' : 'checkbox'}`}
            onClick={() => {
              if (!isReadOnly) {
                setIsLicensed(false);
                setIsLicenseBrokerage(!isLicensed);
              }
            }}
          />
        )}
        {isLicensed === false && (
          <img
            data-testid="licensed-brokerage-checkbox"
            width="20px"
            src={config.unselectedCheckBox}
            alt="uncheckBox"
            className={`${isReadOnly ? 'checkbox-disabled' : 'checkbox'}`}
            onClick={() => {
              if (!isReadOnly) {
                setIsLicensed(true);
                setIsLicenseBrokerage(!isLicensed);
              }
            }}
          />
        )}
        <Box className={`${isReadOnly ? 'text disabled-text' : 'text'}`}>
          This is a licensed brokerage
        </Box>
      </Stack>
    </div>
  );
};

export default ProducerBrokerageInfo;
